import React, { useState, useEffect } from 'react';
import { getStats, getImportHistory, getPlanningHistory, triggerPlanningSnapshot } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
function fmtDate(s) { const [y,m,d] = s.split('-'); return `${d}/${m}/${y}`; }
function todayStr() { return new Date().toISOString().split('T')[0]; }
function addDays(dateStr, n) { const d = new Date(dateStr); d.setDate(d.getDate()+n); return d.toISOString().split('T')[0]; }
function monthLabel(ym) { const [y,m] = ym.split('-'); return `${MOIS_FR[parseInt(m,10)-1]} ${y}`; }

// ─── Bar chart ────────────────────────────────────────────────────────────────
function BarChart({ data }) {
  if (!data || data.length === 0) return <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>Aucune donnée</div>;
  const max = Math.max(...data.map(d => d.interventions), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 140, padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'flex-end', height: 110 }}>
            <div style={{ width: '100%', height: Math.max((d.cleared / max) * 100, d.cleared > 0 ? 4 : 0), background: 'var(--green)', borderRadius: '4px 4px 0 0' }} title={`CLEARED: ${d.cleared}`} />
            <div style={{ width: '100%', height: Math.max((d.failed / max) * 100, d.failed > 0 ? 2 : 0), background: 'var(--accent)', borderRadius: 0 }} title={`FAILED: ${d.failed}`} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{d.mois}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Suivi commandes reçues ───────────────────────────────────────────────────
function CommandesRecuesTab() {
  const [period, setPeriod]   = useState('week');
  const [selMonth, setSelMonth] = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths]   = useState([]);

  const load = async (p, m) => {
    setLoading(true);
    try {
      const today = todayStr();
      let from, to;
      if (p === 'today')       { from = today; to = today; }
      else if (p === 'week')   { from = addDays(today, -6); to = today; }
      else if (p === 'month')  { from = today.substring(0, 7) + '-01'; to = today; }
      else if (p === 'custom' && m) { from = `${m}-01`; to = `${m}-31`; }
      else                     { from = addDays(today, -29); to = today; }

      const d = await getImportHistory(from, to);
      setData(d);
      if (d.availableMonths?.length) {
        setMonths(d.availableMonths);
        if (!m && d.availableMonths.length) setSelMonth(d.availableMonths[0]);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    getImportHistory().then(d => {
      if (d.availableMonths?.length) { setMonths(d.availableMonths); setSelMonth(d.availableMonths[0]); }
    }).catch(() => {});
    load('week');
  }, []); // eslint-disable-line

  const handlePeriod = (p) => { setPeriod(p); load(p, selMonth); };
  const handleMonth  = (m) => { setSelMonth(m); setPeriod('custom'); load('custom', m); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['today', "Aujourd'hui"], ['week', '7 derniers jours'], ['month', 'Ce mois']].map(([p, lbl]) => (
          <button key={p} onClick={() => handlePeriod(p)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
            background: period === p ? 'var(--accent)' : 'var(--bg3)',
            color: period === p ? '#fff' : 'var(--text3)',
          }}>{lbl}</button>
        ))}
        {months.length > 0 && (
          <select value={period === 'custom' ? selMonth : ''} onChange={e => handleMonth(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: '1px solid var(--border2)', background: period === 'custom' ? 'var(--accent)' : 'var(--bg3)', color: period === 'custom' ? '#fff' : 'var(--text3)', cursor: 'pointer' }}>
            <option value="" disabled>Mois spécifique…</option>
            {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        )}
      </div>

      {loading && <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Chargement…</div>}

      {!loading && data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { val: data.total,   lbl: 'Total reçu',            color: 'var(--text)',   bg: 'var(--bg2)',   border: 'var(--border)' },
              { val: data.created, lbl: 'Nouvelles interventions', color: '#16a34a',      bg: '#f0fdf4',      border: '#bbf7d0' },
              { val: data.updated, lbl: 'Mises à jour',           color: '#2563eb',      bg: '#eff6ff',      border: '#bfdbfe' },
            ].map((s, i) => (
              <div key={i} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          {data.days.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              Aucune commande reçue sur cette période
            </div>
          ) : (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 130px 110px', padding: '8px 16px', background: 'var(--bg3)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid var(--border)' }}>
                <div>Date</div><div style={{ textAlign: 'center' }}>Total</div><div style={{ textAlign: 'center' }}>Nouvelles</div><div style={{ textAlign: 'center' }}>Mises à jour</div>
              </div>
              {data.days.map(day => (
                <div key={day.date} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 130px 110px', padding: '12px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(day.date)}</div>
                  <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{day.total}</div>
                  <div style={{ textAlign: 'center' }}>
                    {day.created > 0 ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '2px 10px', borderRadius: 12 }}>+{day.created}</span> : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {day.updated > 0 ? <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '2px 10px', borderRadius: 12 }}>{day.updated}</span> : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                  </div>
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 130px 110px', padding: '10px 16px', background: 'var(--bg3)', borderTop: '2px solid var(--border)', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Total période</div>
                <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 14 }}>{data.total}</div>
                <div style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{data.created}</div>
                <div style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{data.updated}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Suivi planification journalière ─────────────────────────────────────────
function PlanificationTab() {
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [snapping, setSnapping] = useState('');
  const [snapMsg, setSnapMsg]   = useState('');

  const load = async () => {
    setLoading(true);
    try { const d = await getPlanningHistory(); setHistory(d.history || []); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const triggerSnap = async (type) => {
    setSnapping(type); setSnapMsg('');
    try {
      const r = await triggerPlanningSnapshot(type);
      setSnapMsg(`✓ Snapshot ${type === 'morning' ? 'matin' : 'soir'} pris : ${r.count} "A planifier"`);
      await load();
    } catch(e) { setSnapMsg(`✗ Erreur : ${e.message}`); }
    finally { setSnapping(''); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
        <strong>Comment ça marche :</strong> Snapshot automatique à <strong>6h</strong> (début de journée) et <strong>22h</strong> (fin).
        Traité = Début + Commandes reçues − Fin.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>Snapshot manuel :</span>
        <button className="btn btn-secondary btn-sm" disabled={!!snapping} onClick={() => triggerSnap('morning')}>
          {snapping === 'morning' ? '⏳…' : '🌅 Début de journée'}
        </button>
        <button className="btn btn-secondary btn-sm" disabled={!!snapping} onClick={() => triggerSnap('evening')}>
          {snapping === 'evening' ? '⏳…' : '🌆 Fin de journée'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>↻</button>
        {snapMsg && <span style={{ fontSize: 12, color: snapMsg.startsWith('✓') ? '#16a34a' : '#ef4444', fontWeight: 600 }}>{snapMsg}</span>}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>Chargement…</div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text3)', padding: '40px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
          <div style={{ fontWeight: 600 }}>Aucun snapshot enregistré</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Snapshots automatiques à 6h et 22h · Ou utilisez les boutons manuels ci-dessus</div>
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 110px 120px 90px 110px 120px', padding: '8px 16px', background: 'var(--bg3)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid var(--border)' }}>
            <div>Date</div>
            <div style={{ textAlign: 'center' }}>Début (6h)</div>
            <div style={{ textAlign: 'center' }}>Reçues</div>
            <div style={{ textAlign: 'center' }}>Fin (22h)</div>
            <div style={{ textAlign: 'center' }}>Traité</div>
            <div style={{ textAlign: 'center' }}>Statut</div>
          </div>
          {history.map(e => {
            const complete = e.morning_count !== null && e.evening_count !== null;
            return (
              <div key={e.date} style={{ display: 'grid', gridTemplateColumns: '110px 110px 120px 90px 110px 120px', padding: '12px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtDate(e.date)}</div>
                <div style={{ textAlign: 'center' }}>
                  {e.morning_count !== null ? <span style={{ fontSize: 13, fontWeight: 700 }}>{e.morning_count}</span>
                    : <span style={{ color: '#f59e0b', fontSize: 11 }}>En attente</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {e.imports > 0
                    ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: 10 }}>+{e.imports}</span>
                    : <span style={{ color: 'var(--text3)', fontSize: 12 }}>0</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {e.evening_count !== null ? <span style={{ fontSize: 13, fontWeight: 700 }}>{e.evening_count}</span>
                    : <span style={{ color: '#f59e0b', fontSize: 11 }}>En attente</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {complete
                    ? <span style={{ fontSize: 14, fontWeight: 800, color: '#2563eb', background: '#eff6ff', padding: '3px 12px', borderRadius: 12 }}>{e.planifiees}</span>
                    : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {complete
                    ? <span style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: 8 }}>✓ Complet</span>
                    : <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: '#fffbe6', padding: '2px 8px', borderRadius: 8 }}>⏳ Partiel</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
        <strong>Traité</strong> = Début + Commandes reçues − Fin
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function Stats() {
  const [mainTab, setMainTab] = useState('stats');
  const [subTab, setSubTab]   = useState('commandes');

  // Stats CR
  const [period, setPeriod]         = useState('month');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selMonth, setSelMonth]     = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mainTab !== 'stats') return;
    setLoading(true);
    getStats(period, typeFilter, period === 'specific' ? selMonth : undefined)
      .then(d => {
        setData(d);
        if (d.availableMonths?.length && !availableMonths.length) {
          setAvailableMonths(d.availableMonths);
          if (!selMonth) setSelMonth(d.availableMonths[0]);
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, typeFilter, selMonth, mainTab]); // eslint-disable-line

  const handlePeriod = (p) => { setPeriod(p); };
  const handleMonth  = (m) => { setSelMonth(m); setPeriod('specific'); };

  const t       = data?.totaux || {};
  const parTech = data?.par_technicien || [];
  const parMois = data?.par_mois || [];
  const totalType = (t.cri || 0) + (t.sav || 0) + (t.vt || 0);
  const pct = (n) => totalType > 0 ? Math.round((n / totalType) * 100) : 0;

  const TYPE_BTNS = [
    { key: 'all', label: 'Tous types' },
    { key: 'cri', label: 'Production (CRI)' },
    { key: 'vt',  label: 'Visite Technique' },
    { key: 'sav', label: 'SAV' },
  ];

  const PERIOD_BTNS = [
    { key: 'week',    label: 'Cette semaine' },
    { key: 'month',   label: 'Ce mois' },
    { key: 'quarter', label: 'Ce trimestre' },
    { key: 'year',    label: 'Cette année' },
  ];

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Statistiques</div>
      </div>

      <div className="page">
        {/* ── Onglets principaux ── */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 10, padding: 4, alignSelf: 'flex-start', marginBottom: 20 }}>
          {[['stats', '📈 Statistiques CR'], ['ops', '📋 Suivi opérationnel']].map(([k, l]) => (
            <button key={k} onClick={() => setMainTab(k)} style={{
              padding: '7px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
              background: mainTab === k ? 'var(--bg2)' : 'none',
              color: mainTab === k ? 'var(--text)' : 'var(--text3)',
              boxShadow: mainTab === k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}>{l}</button>
          ))}
        </div>

        {/* ══ ONGLET STATISTIQUES CR ══ */}
        {mainTab === 'stats' && (
          <>
            {/* ── Filtres période + type ── */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
              {/* Période */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Période</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {PERIOD_BTNS.map(({ key, label }) => (
                    <button key={key} onClick={() => handlePeriod(key)} style={{
                      padding: '6px 13px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: period === key ? '#185FA5' : 'var(--bg3)',
                      color: period === key ? '#fff' : 'var(--text3)',
                      transition: 'all .15s',
                    }}>{label}</button>
                  ))}
                  {/* Sélecteur de mois */}
                  <select
                    value={period === 'specific' ? selMonth : ''}
                    onChange={e => handleMonth(e.target.value)}
                    style={{
                      padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: '1px solid var(--border2)', cursor: 'pointer',
                      background: period === 'specific' ? '#185FA5' : 'var(--bg3)',
                      color: period === 'specific' ? '#fff' : 'var(--text3)',
                    }}
                  >
                    <option value="" disabled>Mois spécifique…</option>
                    {availableMonths.map(m => (
                      <option key={m} value={m}>{monthLabel(m)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch', margin: '0 4px' }} />

              {/* Type */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Type d'intervention</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {TYPE_BTNS.map(({ key, label }) => (
                    <button key={key} onClick={() => setTypeFilter(key)} style={{
                      padding: '6px 13px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: typeFilter === key
                        ? key === 'sav' ? '#D97706' : key === 'vt' ? '#059669' : key === 'cri' ? '#2563EB' : '#185FA5'
                        : 'var(--bg3)',
                      color: typeFilter === key ? '#fff' : 'var(--text3)',
                      transition: 'all .15s',
                    }}>{label}</button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Chargement…</div>
          ) : (
            <>
              <div className="stats-grid">
                {[
                  { val: t.total ?? '—', lbl: 'Interventions totales', color: 'var(--text)' },
                  { val: t.cleared ?? '—', lbl: 'CLEARED', color: 'var(--green2)' },
                  { val: t.failed ?? '—', lbl: 'FAILED', color: '#ff6b6b' },
                  { val: t.taux != null ? `${t.taux}%` : '—', lbl: 'Taux de réussite', color: 'var(--amber2)' },
                ].map((s, i) => (
                  <div key={i} className="stat-card">
                    <div className="val" style={{ color: s.color }}>{s.val}</div>
                    <div className="lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div className="card">
                  <div style={{ fontWeight: 700, marginBottom: 16 }}>Interventions par mois</div>
                  <BarChart data={parMois} />
                  <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, background: 'var(--green)', borderRadius: 2 }} />CLEARED</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, background: 'var(--accent)', borderRadius: 2 }} />FAILED</div>
                  </div>
                </div>

                <div className="card">
                  <div style={{ fontWeight: 700, marginBottom: 16 }}>Répartition par type</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Raccordement (CRI)', count: t.cri || 0, color: 'var(--blue2)', pct: pct(t.cri || 0) },
                      { label: 'SAV', count: t.sav || 0, color: 'var(--amber2)', pct: pct(t.sav || 0) },
                      { label: 'Visite Technique', count: t.vt || 0, color: 'var(--green2)', pct: pct(t.vt || 0) },
                    ].map((item, i) => (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
                          <span style={{ color: 'var(--text2)' }}>{item.label}</span>
                          <span style={{ fontWeight: 600 }}>{item.count} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({item.pct}%)</span></span>
                        </div>
                        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${item.pct}%`, height: '100%', background: item.color, borderRadius: 3, transition: 'width .6s ease' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: 20, padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px 0', fontWeight: 700 }}>Performance par technicien</div>
                {parTech.length === 0 ? (
                  <div style={{ padding: '20px', color: 'var(--text3)', fontSize: 13 }}>Aucun CR sur la période sélectionnée</div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Technicien</th><th>Total</th><th>CLEARED</th><th>FAILED</th><th>VT</th><th>SAV</th><th>Taux réussite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parTech.map((tech, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{tech.nom}</td>
                            <td>{tech.total}</td>
                            <td><span className="badge badge-green">{tech.cleared}</span></td>
                            <td><span className="badge badge-red">{tech.failed}</span></td>
                            <td><span className="badge badge-blue">{tech.vt}</span></td>
                            <td><span className="badge badge-amber">{tech.sav}</span></td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ flex: 1, height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden', maxWidth: 80 }}>
                                  <div style={{ width: `${tech.taux}%`, height: '100%', background: tech.taux >= 90 ? 'var(--green)' : 'var(--amber)', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 13, color: tech.taux >= 90 ? 'var(--green2)' : 'var(--amber2)' }}>{tech.taux}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
          </>
        )}

        {/* ══ ONGLET SUIVI OPÉRATIONNEL ══ */}
        {mainTab === 'ops' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Sous-onglets */}
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, padding: 3, alignSelf: 'flex-start' }}>
              {[['commandes', '📬 Commandes reçues'], ['planification', '🗓 Planification journalière']].map(([k, l]) => (
                <button key={k} onClick={() => setSubTab(k)} style={{
                  padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: subTab === k ? 'var(--bg2)' : 'none',
                  color: subTab === k ? 'var(--text)' : 'var(--text3)',
                  boxShadow: subTab === k ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>{l}</button>
              ))}
            </div>
            {subTab === 'commandes'    && <CommandesRecuesTab />}
            {subTab === 'planification' && <PlanificationTab />}
          </div>
        )}
      </div>
    </>
  );
}
